// src/pages/Cortes.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Cortes.css";
import Toast from "../components/Toast";
import { authFetch } from "../utils/authFetch";
import { Boton, EncabezadoPagina, Iconos, Insignia } from "../componentes/base";

/* =========================
   Hook: detectar móvil
========================= */
function useIsMobile(breakpoint = 560) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpoint]);

  return isMobile;
}

/* =========================
   Helpers
========================= */
function getNowMY() {
  const d = new Date();
  return { mes: d.getMonth() + 1, anio: d.getFullYear() };
}

function mesNombre(m) {
  const nombres = [
    "Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
  ];
  return nombres[(m || 1) - 1] || "Mes";
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* =========================
   Modal confirmación (custom)
========================= */
function ConfirmModal({
  open,
  title,
  message,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="cortes-confirm-overlay" onMouseDown={onCancel}>
      <div className="cortes-confirm-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cortes-confirm-head">
          <h3 className="cortes-confirm-title">{title || "Confirmar"}</h3>
        </div>

        <div className="cortes-confirm-body">
          <p className="cortes-confirm-text">{message}</p>
        </div>

        <div className="cortes-confirm-actions">
          <button className="cortes-confirm-cancel" type="button" onClick={onCancel}>
            {cancelText}
          </button>
          <button className="cortes-confirm-ok" type="button" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Cards móvil: FIBRA
========================= */
function MorososFibraCards({ rows = [], selected = new Set(), onToggle }) {
  if (!rows.length) {
    return <div className="cortes-empty">No hay clientes morosos con ese filtro</div>;
  }

  return (
    <div className="cortes-cards">
      {rows.map((r) => {
        const isSel = selected.has(r.cliente_id);
        return (
          <button
            key={r.cliente_id}
            className={cn("cortes-carditem", isSel && "selected")}
            onClick={() => onToggle(r.cliente_id)}
            type="button"
          >
            <div className="cortes-carditem-top">
              <div className="cortes-carditem-title">
                <span className={cn("cortes-check", isSel && "on")} />
                <span>#{r.cliente_id} {r.nombre} {r.apellidos}</span>
              </div>
              <div className={cn("cortes-badge", r.equipo)}>
                {r.equipo === "propio" ? "Propio" : "Prestado"}
              </div>
            </div>

            <div className="cortes-carditem-grid">
              <div className="cortes-kv">
                <span className="cortes-k">Plan</span>
                <span className="cortes-v">{r.plan || "—"}</span>
              </div>

              <div className="cortes-kv">
                <span className="cortes-k">Estado</span>
                <span className="cortes-v">{r.estado || "—"}</span>
              </div>

              <div className="cortes-kv cortes-kv-full">
                <span className="cortes-k">Dirección</span>
                <span className="cortes-v">{r.direccion || "—"}</span>
              </div>

              <div className="cortes-kv">
                <span className="cortes-k">Puerto</span>
                <span className="cortes-v">{r.port || "—"}</span>
              </div>

              <div className="cortes-kv">
                <span className="cortes-k">ONTID</span>
                <span className="cortes-v">{r.onu_id ?? "—"}</span>
              </div>

              <div className="cortes-kv cortes-kv-full">
                <span className="cortes-k">OLT</span>
                <span className="cortes-v">{r.olt_id ?? "—"}</span>
              </div>
            </div>

            {r.warning && <div className="cortes-warn">{r.warning}</div>}
          </button>
        );
      })}
    </div>
  );
}

/* =========================
   Cards móvil: WIFI
========================= */
function MorososWifiCards({ rows = [], selected = new Set(), onToggle }) {
  if (!rows.length) {
    return <div className="cortes-empty">No hay clientes morosos con ese filtro</div>;
  }

  return (
    <div className="cortes-cards">
      {rows.map((r) => {
        const isSel = selected.has(r.cliente_id);
        return (
          <button
            key={r.cliente_id}
            className={cn("cortes-carditem", isSel && "selected")}
            onClick={() => onToggle(r.cliente_id)}
            type="button"
          >
            <div className="cortes-carditem-top">
              <div className="cortes-carditem-title">
                <span className={cn("cortes-check", isSel && "on")} />
                <span>#{r.cliente_id} {r.nombre} {r.apellidos}</span>
              </div>
              <div className={cn("cortes-badge", r.equipo)}>
                {r.equipo === "propio" ? "Propio" : "Prestado"}
              </div>
            </div>

            <div className="cortes-carditem-grid">
              <div className="cortes-kv">
                <span className="cortes-k">Plan</span>
                <span className="cortes-v">{r.plan || "—"}</span>
              </div>

              <div className="cortes-kv">
                <span className="cortes-k">Estado</span>
                <span className="cortes-v">{r.estado || "—"}</span>
              </div>

              <div className="cortes-kv cortes-kv-full">
                <span className="cortes-k">IP</span>
                <span className="cortes-v">{r.ip || "—"}</span>
              </div>

              <div className="cortes-kv cortes-kv-full">
                <span className="cortes-k">MKT ID</span>
                <span className="cortes-v">{r.mkt_id ?? "—"}</span>
              </div>
            </div>

            {r.warning && <div className="cortes-warn">{r.warning}</div>}
          </button>
        );
      })}
    </div>
  );
}

async function fetchJSON(url, options = {}) {
  const res = await authFetch(url, options);
  const text = await res.text();

  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    throw new Error(`HTTP ${res.status} respuesta no-JSON: ${text.slice(0, 300)}`);
  }

  if (!res.ok) {
    const msg = (data && (data.error || data.mensaje)) ? (data.error || data.mensaje) : "Error HTTP";
    throw new Error(`HTTP ${res.status}: ${msg}`);
  }

  return data;
}

/* =========================
   Progreso JOB (slot + actual)
========================= */
function JobProgress({ jobInfo }) {
  if (!jobInfo) return null;

  const total = Number(jobInfo.total ?? 0);
  const pendientes = Number(jobInfo.pendientes ?? 0);
  const procesando = Number(jobInfo.procesando ?? 0);
  const ok = Number(jobInfo.exitosos ?? 0);
  const fail = Number(jobInfo.fallidos ?? 0);

  const doneCount = ok + fail;
  const pct = total > 0 ? Math.min(100, Math.round((doneCount / total) * 100)) : 0;

  const current = jobInfo.current || null;
  const groups = Array.isArray(jobInfo.groups) ? jobInfo.groups : [];

  return (
    <div className="cortes-jobbox">
      <div className="cortes-jobbox-head">
        <div>
          <h3 className="cortes-jobbox-title">Progreso del corte (FIBRA)</h3>
          <p className="cortes-jobbox-sub">
            {pct}% · Total {total} · P {pendientes} · PR {procesando} · OK {ok} · ERR {fail}
          </p>
        </div>

        <div className={cn("cortes-job-badge", (fail > 0 && !jobInfo.done) && "warn", jobInfo.done && "done")}>
          {jobInfo.done ? "Terminado" : "En proceso"}
        </div>
      </div>

      <div className="cortes-jobbar">
        <div className="cortes-jobbar-fill" style={{ width: `${pct}%` }} />
      </div>

      {current ? (
        <div className="cortes-job-current">
          <span className="dot" />
          <span>
            Procesando: OLT {current.olt_id ?? "—"} · GPON {current.frame}/{current.slot} · PON {current.pon} · ONT {current.onu_id} · Cliente #{current.cliente_id}
          </span>
        </div>
      ) : (
        <div className="cortes-job-current muted">
          <span className="dot" />
          <span>Preparando / esperando siguiente cliente...</span>
        </div>
      )}

      <div className="cortes-jobslots">
        {groups.length === 0 ? (
          <div className="cortes-empty">Sin detalle por slot</div>
        ) : (
          groups.map((g, idx) => {
            const t = Number(g.total ?? 0);
            const p = Number(g.pendientes ?? 0);
            const pr = Number(g.procesando ?? 0);
            const ok2 = Number(g.exitosos ?? 0);
            const er = Number(g.fallidos ?? 0);
            const d = ok2 + er;
            const pct2 = t > 0 ? Math.min(100, Math.round((d / t) * 100)) : 0;

            const isCurrent =
              current &&
              Number(current.olt_id) === Number(g.olt_id) &&
              Number(current.frame) === Number(g.frame) &&
              Number(current.slot) === Number(g.slot);

            return (
              <div className={cn("cortes-jobslot", isCurrent && "current")} key={`${g.olt_id}-${g.frame}-${g.slot}-${idx}`}>
                <div className="cortes-jobslot-top">
                  <div className="cortes-jobslot-title">
                    <span className={cn("pill", isCurrent && "on")}>OLT {g.olt_id}</span>
                    <span className="pill">GPON {g.frame}/{g.slot}</span>
                  </div>
                  <div className="cortes-jobslot-kpis">
                    <span>P:{p}</span>
                    <span>PR:{pr}</span>
                    <span>OK:{ok2}</span>
                    <span>ERR:{er}</span>
                  </div>
                </div>

                <div className="cortes-jobslot-bar">
                  <div className="cortes-jobslot-barfill" style={{ width: `${pct2}%` }} />
                </div>

                <div className="cortes-jobslot-foot">
                  <span>{pct2}%</span>
                  <span>Total: {t}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function Cortes() {
  const isMobile = useIsMobile(560);

  const API_BASE = "/api";

  const now = getNowMY();
  const [mes, setMes] = useState(now.mes);
  const [anio, setAnio] = useState(now.anio);
  const [equipo, setEquipo] = useState("propio");
  const [conexion, setConexion] = useState("FIBRA");

  const [rowsFibra, setRowsFibra] = useState([]);
  const [rowsWifi, setRowsWifi] = useState([]);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [selectedFibra, setSelectedFibra] = useState(() => new Set());
  const [selectedWifi, setSelectedWifi] = useState(() => new Set());

  const isFibra = conexion === "FIBRA";

  const rows = isFibra ? rowsFibra : rowsWifi;
  const selected = isFibra ? selectedFibra : selectedWifi;

  const selectedCount = selected.size;
  const total = rows.length;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [pendingIds, setPendingIds] = useState([]);

  const [jobId, setJobId] = useState(null);
  const [jobInfo, setJobInfo] = useState(null);
  const jobTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const canCut = useMemo(() => selectedCount > 0, [selectedCount]);

  function toggleSelect(id) {
    if (isFibra) {
      setSelectedFibra((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedWifi((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  }

  function selectAll() {
    if (isFibra) setSelectedFibra(new Set(rowsFibra.map((r) => r.cliente_id)));
    else setSelectedWifi(new Set(rowsWifi.map((r) => r.cliente_id)));
  }

  function clearSelection() {
    if (isFibra) setSelectedFibra(new Set());
    else setSelectedWifi(new Set());
  }

  async function buscar() {
    try {
      setLoading(true);
      clearSelection();

      const endpoint = isFibra ? "cortes/morosos" : "cortes/morosos_wifi";
      const url = `${API_BASE}/${endpoint}?mes=${mes}&anio=${anio}&equipo=${encodeURIComponent(equipo)}`;

      const data = await fetchJSON(url);

      if (data.ok) {
        if (isFibra) {
          // Vista ordenada por # de cliente ascendente (solo visual).
          // El corte por bloques OLT no depende de este orden: el backend
          // reagrupa por OLT/tarjeta/puerto al ejecutar el job.
          const ordenadas = [...(data.rows || [])].sort(
            (a, b) => Number(a.cliente_id) - Number(b.cliente_id)
          );
          setRowsFibra(ordenadas);
        } else {
          setRowsWifi(data.rows || []);
        }
      } else {
        if (isFibra) setRowsFibra([]);
        else setRowsWifi([]);
        setToast({ mensaje: data.error || "Error consultando morosos", tipo: "error" });
      }
    } catch (e) {
      console.log("ERROR cortes buscar:", e);
      if (isFibra) setRowsFibra([]);
      else setRowsWifi([]);
      setToast({ mensaje: "Error de red consultando morosos", tipo: "error" });
    } finally {
      setLoading(false);
    }
  }

  function pedirConfirmacionCorte() {
    if (!canCut) return;

    const ids = Array.from(selected);
    setPendingIds(ids);

    setConfirmMsg(
      `Vas a cortar ${ids.length} cliente(s) de ${mesNombre(mes)} ${anio} (${equipo}) · ${conexion}.\n\n¿Continuar?`
    );
    setConfirmOpen(true);
  }

  function detenerPollingJob() {
    if (jobTimerRef.current) {
      clearInterval(jobTimerRef.current);
      jobTimerRef.current = null;
    }
  }

  async function iniciarPollingJob(nuevoJobId) {
    detenerPollingJob();
    setJobId(nuevoJobId);

    // estructura inicial (ya con campos nuevos)
    setJobInfo({
      total: 0,
      pendientes: 0,
      procesando: 0,
      exitosos: 0,
      fallidos: 0,
      groups: [],
      current: null,
      done: false,
    });

    jobTimerRef.current = setInterval(async () => {
      try {
        const st = await fetchJSON(
          `${API_BASE}/cortes/job/estado/${encodeURIComponent(nuevoJobId)}?mes=${mes}&anio=${anio}`
        );

        if (!mountedRef.current) return;

        if (st.ok) {
          setJobInfo(st);

          if (st.done) {
            detenerPollingJob();
            setToast({
              mensaje: `Corte terminado · OK=${st.exitosos} · ERROR=${st.fallidos}`,
              tipo: st.fallidos > 0 ? "error" : "success",
            });

            await buscar();

            setJobId(null);
            setJobInfo(null);
          }
        }
      } catch (e) {
        console.log("WARN polling job:", e);
      }
    }, 1200); // un poco más ágil
  }

  async function ejecutarCorte() {
    const ids = pendingIds || [];
    setConfirmOpen(false);

    if (!ids.length) return;

    try {
      setLoading(true);

      if (!isFibra) {
        const data = await fetchJSON(`${API_BASE}/cortes/ejecutar_wifi`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mes, anio, equipo, cliente_ids: ids }),
        });

        if (data.ok) {
          setToast({
            mensaje: data.mensaje || `Cortes ejecutados: ${data.exitosos ?? "—"} OK`,
            tipo: "success",
          });
          await buscar();
        } else {
          setToast({ mensaje: data.error || "No se pudo ejecutar el corte", tipo: "error" });
        }

        return;
      }

      const job = await fetchJSON(`${API_BASE}/cortes/job/crear`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mes, anio, equipo, cliente_ids: ids }),
      });

      if (!job.ok) {
        setToast({ mensaje: job.error || "No se pudo crear el job de corte", tipo: "error" });
        return;
      }

      setToast({
        mensaje: `Job creado. Ejecutando corte en segundo plano... (${job.total ?? "—"} clientes)`,
        tipo: "success",
      });

      await iniciarPollingJob(job.job_id);
    } catch (e) {
      console.log("ERROR cortes ejecutar:", e);
      setToast({ mensaje: "Error de red ejecutando cortes", tipo: "error" });
    } finally {
      setLoading(false);
      setPendingIds([]);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      detenerPollingJob();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedFibra(new Set());
    setSelectedWifi(new Set());
    detenerPollingJob();
    setJobId(null);
    setJobInfo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conexion]);

  return (
    <div className="cortes-page">
      <EncabezadoPagina
        icono={Iconos.tijeras}
        titulo="Cortes"
        resumen={
          total
            ? `${total} morosos en ${mesNombre(mes)} ${anio}` +
              (selectedCount ? ` · ${selectedCount} seleccionados` : "")
            : `${mesNombre(mes)} ${anio} · ${conexion}`
        }
      />

      <div className="cortes-hero">

        <div className="cortes-toolbar">
          <div className="cortes-pill">
            <label>Mes</label>
            <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {Array.from({ length: 12 }).map((_, i) => {
                const m = i + 1;
                return (
                  <option value={m} key={m}>
                    {m} · {mesNombre(m)}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="cortes-pill">
            <label>Año</label>
            <input
              type="number"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
              min="2020"
              max="2100"
            />
          </div>

          <div className="cortes-pill">
            <label>Conexión</label>
            <select value={conexion} onChange={(e) => setConexion(e.target.value)}>
              <option value="FIBRA">FIBRA</option>
              <option value="WIFI">WIFI</option>
            </select>
          </div>

          <div className="cortes-seg">
            <button
              type="button"
              className={cn("seg-btn", equipo === "propio" && "active")}
              onClick={() => setEquipo("propio")}
            >
              Propios
            </button>
            <button
              type="button"
              className={cn("seg-btn", equipo === "prestado" && "active")}
              onClick={() => setEquipo("prestado")}
            >
              Prestados
            </button>
          </div>

          <Boton icono={Iconos.buscar} onClick={buscar} type="button" disabled={!!jobId}>
            Buscar
          </Boton>
        </div>

        <div className="cortes-stats">
          <div className="stat-card">
            <span className="stat-label">Periodo</span>
            <span className="stat-value">{mesNombre(mes)} {anio}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">Conexión</span>
            <span className="stat-value">{conexion}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">Equipo</span>
            <span className="stat-value">{equipo === "propio" ? "Propio" : "Prestado"}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">Morosos</span>
            <span className="stat-value">{total}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">Seleccionados</span>
            <span className="stat-value">{selectedCount}</span>
          </div>

          {jobInfo ? (
            <div className="stat-card">
              <span className="stat-label">Job</span>
              <span className="stat-value">
                P:{jobInfo.pendientes ?? 0} · PR:{jobInfo.procesando ?? 0} · OK:{jobInfo.exitosos ?? 0} · ERR:{jobInfo.fallidos ?? 0}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* ✅ PANEL PROGRESO REAL (solo cuando hay job) */}
      {jobInfo ? <JobProgress jobInfo={jobInfo} /> : null}

      <div className="cortes-actions">
        <div className="cortes-actions-left">
          <Boton
            variante="secundario"
            icono={Iconos.confirmar}
            type="button"
            onClick={selectAll}
            disabled={!rows.length || !!jobId}
          >
            Seleccionar todo
          </Boton>
          <Boton
            variante="secundario"
            icono={Iconos.cerrar}
            type="button"
            onClick={clearSelection}
            disabled={!selectedCount || !!jobId}
          >
            Limpiar
          </Boton>
        </div>

        {/* Acción destructiva: en rojo pleno y con el número de afectados a
            la vista, para que nadie corte a 300 clientes creyendo que eran 3. */}
        <Boton
          variante="peligro"
          icono={Iconos.cortes}
          type="button"
          onClick={pedirConfirmacionCorte}
          disabled={!canCut || !!jobId}
          title={
            !!jobId
              ? "Hay un proceso de cortes en ejecución"
              : !canCut
              ? "Selecciona al menos un cliente"
              : "Ejecutar cortes"
          }
        >
          Cortar {selectedCount || ""} seleccionado{selectedCount === 1 ? "" : "s"}
        </Boton>
      </div>

      <div className="cortes-card">
        <div className="cortes-card-header">
          <h2>Clientes morosos · {conexion}</h2>
          <Insignia variante="info" texto={`${total} encontrados`} icono={Iconos.clientes} />
        </div>

        {isMobile ? (
          isFibra ? (
            <MorososFibraCards rows={rowsFibra} selected={selectedFibra} onToggle={toggleSelect} />
          ) : (
            <MorososWifiCards rows={rowsWifi} selected={selectedWifi} onToggle={toggleSelect} />
          )
        ) : (
          <div className="cortes-table-wrapper">
            {isFibra ? (
              <table className="cortes-table">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}></th>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Plan</th>
                    <th>Estado</th>
                    <th>Equipo</th>
                    <th>Puerto</th>
                    <th>ONTID</th>
                    <th>OLT</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsFibra.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="cortes-empty">
                        No hay clientes morosos con ese filtro
                      </td>
                    </tr>
                  ) : (
                    rowsFibra.map((r) => {
                      const isSel = selectedFibra.has(r.cliente_id);
                      return (
                        <tr key={r.cliente_id} className={isSel ? "selected" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleSelect(r.cliente_id)}
                              disabled={!!jobId}
                            />
                          </td>
                          <td>#{r.cliente_id}</td>
                          <td className="cortes-td-strong">
                            {r.nombre} {r.apellidos}
                            {r.warning ? <span className="cortes-inline-warn"> · {r.warning}</span> : null}
                          </td>
                          <td>{r.plan || "—"}</td>
                          <td>{r.estado || "—"}</td>
                          <td>
                            <span className={cn("cortes-pilltag", r.equipo)}>
                              {r.equipo === "propio" ? "Propio" : "Prestado"}
                            </span>
                          </td>
                          <td>{r.port || "—"}</td>
                          <td>{r.onu_id ?? "—"}</td>
                          <td>{r.olt_id ?? "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            ) : (
              <table className="cortes-table cortes-table-wifi">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}></th>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Plan</th>
                    <th>Estado</th>
                    <th>Equipo</th>
                    <th>IP</th>
                    <th>MKT ID</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsWifi.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="cortes-empty">
                        No hay clientes morosos con ese filtro
                      </td>
                    </tr>
                  ) : (
                    rowsWifi.map((r) => {
                      const isSel = selectedWifi.has(r.cliente_id);
                      return (
                        <tr key={r.cliente_id} className={isSel ? "selected" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleSelect(r.cliente_id)}
                              disabled={!!jobId}
                            />
                          </td>
                          <td>#{r.cliente_id}</td>
                          <td className="cortes-td-strong">
                            {r.nombre} {r.apellidos}
                            {r.warning ? <span className="cortes-inline-warn"> · {r.warning}</span> : null}
                          </td>
                          <td>{r.plan || "—"}</td>
                          <td>{r.estado || "—"}</td>
                          <td>
                            <span className={cn("cortes-pilltag", r.equipo)}>
                              {r.equipo === "propio" ? "Propio" : "Prestado"}
                            </span>
                          </td>
                          <td>{r.ip || "—"}</td>
                          <td>{r.mkt_id ?? "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Confirmar corte · ${conexion}`}
        message={confirmMsg}
        cancelText="Cancelar"
        confirmText="Aceptar"
        onCancel={() => {
          setConfirmOpen(false);
          setPendingIds([]);
        }}
        onConfirm={ejecutarCorte}
      />

      {loading && (
        <div className="cortes-loading-overlay">
          <div className="cortes-loading-card">
            <div className="cortes-spinner" />
            <p>Procesando...</p>
          </div>
        </div>
      )}

      {toast && <Toast mensaje={toast.mensaje} tipo={toast.tipo} onClose={() => setToast(null)} />}
    </div>
  );
}
